import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Empty, Input, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { CopyOutlined, LinkOutlined, PlusOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

import { chaosWidgetApi } from 'src/api';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from 'src/constants';
import { setAppTitle } from 'src/store/app';
import { useDispatch } from 'react-redux';

import './chaosWidget.scss';

const { Text } = Typography;

const normalizeLanguage = (language) => {
  const normalized = String(language || '')
    .toLowerCase()
    .trim();
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : DEFAULT_LANGUAGE;
};

const buildLocalWidgetUrl = (uuid, language = DEFAULT_LANGUAGE) =>
  `${window.location.origin}/${uuid}/chaos-widget?lang=${normalizeLanguage(language)}`;

const buildLocalPreviewUrl = (language = DEFAULT_LANGUAGE) =>
  `${window.location.origin}/preview/chaos-widget?preview=1&lang=${normalizeLanguage(language)}`;

export const ChaosWidgetDashboard = () => {
  const { t, i18n } = useTranslation('chaos_widget');
  const dispatch = useDispatch();
  const [widgets, setWidgets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [language, setLanguage] = useState(() => normalizeLanguage(i18n.resolvedLanguage));
  const [titleDraft, setTitleDraft] = useState('');

  const activeWidget = useMemo(() => widgets[0] || null, [widgets]);

  const fetchWidgets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await chaosWidgetApi.fetchChaosWidgets();
      const list = response?.data?.data || [];
      setWidgets(list);
    } catch (error) {
      toast(error?.response?.data?.message || t('errors.load_failed'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    dispatch(setAppTitle(t('title')));
  }, [dispatch, t]);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  useEffect(() => {
    if (!activeWidget) return;
    setLanguage(normalizeLanguage(activeWidget.language));
    setTitleDraft(activeWidget.title || '');
  }, [activeWidget]);

  const handleCreateWidget = useCallback(async () => {
    setIsCreating(true);
    try {
      const response = await chaosWidgetApi.createChaosWidget();
      const created = response?.data?.data;
      if (created) {
        setWidgets([created]);
        toast.success(t('success.created'));
      }
    } catch (error) {
      toast(error?.response?.data?.message || t('errors.create_failed'), { type: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [t]);

  const handleSaveSettings = useCallback(async () => {
    if (!activeWidget?._id) return;

    try {
      const response = await chaosWidgetApi.updateChaosWidget(activeWidget._id, {
        title: titleDraft,
        language,
        enabled: Boolean(activeWidget.enabled)
      });
      const updated = response?.data?.data;
      if (updated) {
        setWidgets([updated]);
        toast.success(t('success.updated'));
      }
    } catch (error) {
      toast(error?.response?.data?.message || t('errors.update_failed'), { type: 'error' });
    }
  }, [activeWidget, language, t, titleDraft]);

  const handleToggleEnabled = useCallback(
    async (checked) => {
      if (!activeWidget?._id) return;

      try {
        const response = await chaosWidgetApi.updateChaosWidget(activeWidget._id, {
          enabled: Boolean(checked)
        });
        const updated = response?.data?.data;
        if (updated) {
          setWidgets([updated]);
          toast.success(t('success.updated'));
        }
      } catch (error) {
        toast(error?.response?.data?.message || t('errors.update_failed'), { type: 'error' });
      }
    },
    [activeWidget, t]
  );

  const handleCopyLink = useCallback(
    async (url) => {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t('success.copied'));
      } catch (_error) {
        toast.error(t('errors.copy_failed'));
      }
    },
    [t]
  );

  const handleOpenPreview = useCallback(() => {
    window.open(buildLocalPreviewUrl(language), '_blank', 'noopener,noreferrer');
  }, [language]);

  const columns = [
    {
      title: t('table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (value, row) => value || row?.uuid
    },
    {
      title: t('table.status'),
      key: 'status',
      width: 140,
      render: (_, row) => (
        <Tag color={row?.enabled ? 'green' : 'default'}>{row?.enabled ? 'active' : 'disabled'}</Tag>
      )
    },
    {
      title: t('table.link'),
      key: 'url',
      render: (_, row) => {
        const url = buildLocalWidgetUrl(row?.uuid, language);
        return (
          <Space>
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyLink(url)}>
              {t('actions.copy')}
            </Button>
          </Space>
        );
      }
    },
    {
      title: t('table.created_at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '-')
    }
  ];

  return (
    <div className="chaos-widget-dashboard">
      <div className="chaos-widget-dashboard__content">
        <div className="chaos-widget-dashboard__header">
          <Button
            className="chaos-widget-dashboard__create-btn"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateWidget}
            loading={isCreating}
          >
            {t('actions.create')}
          </Button>
        </div>

        <Tabs
          className="chaos-widget-dashboard__tabs"
          items={[
            {
              key: 'settings',
              label: 'Settings',
              children: (
                <Card className="chaos-widget-dashboard__card" bordered={false}>
                  {activeWidget ? (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space size={12} wrap>
                <Tag color="purple" icon={<LinkOutlined />}>
                  {activeWidget.uuid}
                </Tag>
                <Switch
                  checked={Boolean(activeWidget.enabled)}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  onChange={handleToggleEnabled}
                />
              </Space>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">{t('fields.title')}</Text>
                <Input
                  value={titleDraft}
                  maxLength={120}
                  onChange={(event) => setTitleDraft(event.target.value)}
                />
              </Space>

              <Space direction="vertical">
                <Text type="secondary">{t('fields.language')}</Text>
                <Select
                  value={language}
                  onChange={setLanguage}
                  options={SUPPORTED_LANGUAGES.map((lang) => ({
                    value: lang,
                    label: lang.toUpperCase()
                  }))}
                  style={{ minWidth: 120 }}
                />
              </Space>

              <div className="chaos-widget-dashboard__link-row">
                <Text strong>{buildLocalWidgetUrl(activeWidget.uuid, language)}</Text>
                <Space>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyLink(buildLocalWidgetUrl(activeWidget.uuid, language))}
                  >
                    {t('actions.copy')}
                  </Button>
                  <Button type="primary" onClick={handleSaveSettings}>
                    {t('actions.save')}
                  </Button>
                </Space>
              </div>
            </Space>
          ) : (
            <Empty description={t('empty.no_widget')} />
          )}
                </Card>
              )
            },
            {
              key: 'preview',
              label: 'Preview',
              children: (
                <Card className="chaos-widget-dashboard__card chaos-widget-dashboard__preview" bordered={false}>
                  <Space direction="vertical" size={12}>
                    <Text>
                      Opens a local visual preview with simulated reward redemptions. It does not connect to Twitch, the backend, or Chaos Desktop.
                    </Text>
                    <Text type="secondary">{buildLocalPreviewUrl(language)}</Text>
                    <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleOpenPreview}>
                      Open local preview
                    </Button>
                  </Space>
                </Card>
              )
            }
          ]}
        />

        <Card className="chaos-widget-dashboard__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id}
            loading={isLoading}
            columns={columns}
            dataSource={widgets}
            pagination={false}
            locale={{ emptyText: t('empty.no_data') }}
          />
        </Card>
      </div>
    </div>
  );
};
